import { useEffect, useMemo, useState } from 'react'
import { Box, Dialog, DialogContent, DialogTitle } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import PublishIcon from '@mui/icons-material/Publish'
import toast from 'react-hot-toast'
import {
  usePublishedSchedules,
  usePublishSchedule,
  useSheduler,
} from './Scheduler.data'
import {
  groupIdenticalVariants,
  useScheduleVariants,
} from 'Features/SchedulerView/SchedulerView.data'
import { FONT_SANS, PillButton, TOKENS } from './SchedulerDesign'

const WEEKS = [1, 2, 3, 4, 5, 6, 7, 8]

/** `2026-09-21` -> `21.09.2026`, the format the deck's warning uses. */
const ddmmyyyy = (iso: string): string => {
  const [y, m, d] = (iso || '').split('-')
  return y && m && d ? `${d}.${m}.${y}` : iso
}

/** Monday of week N, mirroring `published_schedule_model.week_commencing`.
 *
 *  Computed here as well as server-side because the dialog has to warn BEFORE
 *  it posts anything — it cannot ask the server which weeks would collide
 *  without first telling it what the user picked. The two must agree; if the
 *  server's parsing ever changes, this is the other half. */
const weekCommencing = (scheduleStart: string, week: number): string => {
  const base = new Date(scheduleStart)
  if (Number.isNaN(base.getTime())) return ''
  const d = new Date(base)
  d.setDate(d.getDate() + 7 * (week - 1))
  return d.toISOString().slice(0, 10)
}

type Stage = 'choose' | 'warn-exists' | 'warn-overwrite'

/**
 * Publish a plan — client deck 2026-09-16, slides 5 and 6.
 *
 * Three selections (variant, version, horizon), Publish inert until all three
 * are made, and two confirmations before anything overwrites a week somebody
 * else is already working from.
 *
 * The two warnings are deliberately NOT one. The first asks whether you meant
 * to touch an already-published week at all; the second, after you have been
 * back to the horizon picker and left those weeks in, tells you plainly what
 * Publish is about to do. Collapsing them into a single "are you sure" is how
 * a planner clicks through and overwrites a week they never looked at.
 */
export const PublishScheduleDialog = ({
  open,
  onClose,
  schedulerId,
  iteration,
}: {
  open: boolean
  onClose: () => void
  schedulerId: string
  iteration: string
}) => {
  const [stage, setStage] = useState<Stage>('choose')
  const [variantKey, setVariantKey] = useState('')
  const [version, setVersion] = useState('')
  const [weeks, setWeeks] = useState<number[]>([])

  const { data: schedules } = useSheduler()
  const { data: published = [] } = usePublishedSchedules()
  const { data: variants } = useScheduleVariants(schedulerId, iteration)

  // `useSheduler` selects an untyped payload, so the row shape is named here
  // rather than inferred. Only the three fields this dialog needs.
  type ScheduleRow = {
    id: string
    startDate?: string
    versionDetails?: string | Record<string, { name?: string }>
  }
  const schedule = useMemo(
    () =>
      ((schedules ?? []) as ScheduleRow[]).find((r) => r.id === schedulerId),
    [schedules, schedulerId]
  )
  const scheduleStart = schedule?.startDate ?? ''

  /** Every version this schedule has, newest first. Read off the schedule row
   *  rather than fetched: `versionDetails` is already on it, so a dedicated
   *  endpoint would be a second source of the same truth. */
  const versions = useMemo<{ key: string; name: string }[]>(() => {
    const raw = schedule?.versionDetails
    if (!raw) return [{ key: '0', name: 'Version 0' }]
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
      const list = Object.keys(parsed).map((k) => ({
        key: k,
        name:
          (parsed as Record<string, { name?: string }>)[k]?.name ??
          `Version ${k}`,
      }))
      return list.length
        ? list.sort((a, b) => Number(b.key) - Number(a.key))
        : [{ key: '0', name: 'Version 0' }]
    } catch {
      return [{ key: '0', name: 'Version 0' }]
    }
  }, [schedule])

  /** `weekCommencing -> the publish currently in force`, for the warnings and
   *  for marking weeks in the picker. Only current rows: a superseded publish
   *  is history, not a collision. */
  const currentByWeek = useMemo(() => {
    const m = new Map<string, (typeof published)[number]>()
    for (const p of published)
      if (p.isCurrent && p.weekCommencing) m.set(p.weekCommencing, p)
    return m
  }, [published])

  const clashes = useMemo(
    () =>
      weeks
        .map((w) => ({ week: w, wc: weekCommencing(scheduleStart, w) }))
        .filter((x) => x.wc && currentByWeek.has(x.wc)),
    [weeks, scheduleStart, currentByWeek]
  )

  useEffect(() => {
    if (open) {
      setStage('choose')
      setVariantKey('')
      setVersion(iteration || '0')
      setWeeks([])
    }
  }, [open, iteration])

  const { mutate: publish, isLoading: isPublishing } = usePublishSchedule(
    () => {
      toast.success(
        `Published ${weeks.length} week${weeks.length === 1 ? '' : 's'}`
      )
      onClose()
    },
    () => toast.error('Could not publish this plan')
  )

  const ready = Boolean(variantKey) && Boolean(version) && weeks.length > 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
        Publish plan
      </DialogTitle>
      <DialogContent>
        {stage === 'choose' && (
          <>
            <Field label="Select variant">
              <Select
                value={variantKey}
                onChange={setVariantKey}
                placeholder="Choose an answer…"
                // One entry per distinct schedule, as in the top-strip picker.
                options={groupIdenticalVariants(variants ?? []).map((v) => ({
                  value: v.variant_key,
                  label: v.label ?? v.variant_key,
                }))}
              />
            </Field>
            <Field label="Select version">
              <Select
                value={version}
                onChange={setVersion}
                placeholder="Choose a version…"
                options={versions.map((v) => ({ value: v.key, label: v.name }))}
              />
            </Field>
            <Field label="Select horizon">
              <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {WEEKS.map((w) => {
                  const wc = weekCommencing(scheduleStart, w)
                  const taken = Boolean(wc && currentByWeek.has(wc))
                  const on = weeks.includes(w)
                  return (
                    <Box
                      key={w}
                      component="button"
                      type="button"
                      onClick={() =>
                        setWeeks((prev) =>
                          prev.includes(w)
                            ? prev.filter((x) => x !== w)
                            : [...prev, w]
                        )
                      }
                      title={
                        taken
                          ? `Already published for W/C ${ddmmyyyy(wc)}`
                          : wc
                            ? `W/C ${ddmmyyyy(wc)}`
                            : undefined
                      }
                      sx={{
                        minWidth: 44,
                        height: 34,
                        cursor: 'pointer',
                        borderRadius: '2px',
                        fontFamily: FONT_SANS,
                        fontSize: 11.5,
                        fontWeight: 700,
                        background: on ? TOKENS.brandTeal : TOKENS.surface0,
                        color: on ? '#fff' : TOKENS.ink700,
                        border: `1px solid ${on ? TOKENS.brandTeal : TOKENS.line300}`,
                        // An already-published week is marked, never disabled:
                        // republishing one is a legitimate act the deck
                        // describes, it just has to be deliberate.
                        boxShadow: taken
                          ? `inset 0 -3px 0 0 ${TOKENS.stateRunning}`
                          : 'none',
                      }}
                    >
                      {w}
                    </Box>
                  )
                })}
              </Box>
              <Note>
                A week with an amber underline is already published. You can
                select it — you will be asked to confirm before it is
                overwritten.
              </Note>
            </Field>
          </>
        )}

        {stage === 'warn-exists' && (
          <Warning
            lines={clashes.map(
              (c) => `Schedule for W/C ${ddmmyyyy(c.wc)} already exists.`
            )}
          />
        )}
        {stage === 'warn-overwrite' && (
          <Warning
            lines={[
              'Selecting Publish will overwrite any previously published schedules for the week selected.',
              ...clashes.map((c) => `W/C ${ddmmyyyy(c.wc)}`),
            ]}
          />
        )}
      </DialogContent>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          px: 3,
          pb: 2,
          pt: 1,
        }}
      >
        <PillButton kind="subtle" onClick={onClose}>
          Cancel
        </PillButton>
        {stage !== 'choose' && (
          <PillButton kind="ghost" onClick={() => setStage('choose')}>
            Update horizon
          </PillButton>
        )}
        <PillButton
          kind="primary"
          disabled={!ready || isPublishing}
          onClick={() => {
            if (stage === 'choose' && clashes.length > 0)
              return setStage('warn-exists')
            if (stage === 'warn-exists') return setStage('warn-overwrite')
            publish({ schedulerId, iteration: version, variantKey, weeks })
          }}
          icon={<PublishIcon />}
          title={
            ready
              ? undefined
              : 'Choose a variant, a version and at least one week'
          }
        >
          {stage === 'choose' ? 'Publish' : 'Yes, continue'}
        </PillButton>
      </Box>
    </Dialog>
  )
}

// ── small local pieces ──────────────────────────────────────────────────────

const Field = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <Box sx={{ mb: 2 }}>
    <Box
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: TOKENS.ink500,
        fontFamily: FONT_SANS,
        mb: '6px',
      }}
    >
      {label}
    </Box>
    {children}
  </Box>
)

const Select = ({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  placeholder: string
}) => (
  <Box
    component="select"
    value={value}
    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
      onChange(e.target.value)
    }
    sx={{
      width: '100%',
      height: 32,
      px: '8px',
      borderRadius: '2px',
      border: `1px solid ${TOKENS.line300}`,
      background: TOKENS.surface0,
      fontFamily: FONT_SANS,
      fontSize: 12,
      color: TOKENS.ink900,
    }}
  >
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </Box>
)

const Note = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      mt: '8px',
      fontSize: 11,
      lineHeight: 1.45,
      color: TOKENS.ink400,
      fontFamily: FONT_SANS,
    }}
  >
    {children}
  </Box>
)

const Warning = ({ lines }: { lines: string[] }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1.25,
      p: '12px 14px',
      border: `1px solid ${TOKENS.stateRunning}`,
      borderRadius: '2px',
      background: 'rgba(224,146,47,0.10)',
      fontFamily: FONT_SANS,
      fontSize: 12.5,
      color: TOKENS.ink900,
      lineHeight: 1.5,
    }}
  >
    <WarningAmberIcon sx={{ fontSize: 18, color: TOKENS.stateRunning }} />
    <Box>
      {lines.map((l, i) => (
        <Box key={i} sx={{ mb: i === lines.length - 1 ? 0 : '4px' }}>
          {l}
        </Box>
      ))}
    </Box>
  </Box>
)
